import { useAtom } from '@effect/atom-react';
import { Schema } from 'effect';
import { useEffect } from 'react';
import { ClientFlagValues, FlagStage } from '@listening/api-contract/flags';
import type { FlagsResponse } from '@listening/api-contract/flags-endpoints';
import { atomKvsSync } from '@listening/frontend/atoms/kvs';

const SavedFlags = Schema.Struct({
  /** The stage whose backend sent these values; another stage's are ignored. */
  stage: Schema.NullOr(FlagStage),
  signedOut: Schema.NullOr(ClientFlagValues),
  signedIn: Schema.NullOr(
    Schema.Struct({ userId: Schema.String, flags: ClientFlagValues }),
  ),
});
export type SavedFlags = typeof SavedFlags.Type;

export const noSavedFlags: SavedFlags = {
  stage: null,
  signedOut: null,
  signedIn: null,
};

/**
 * The last `Flags.Get` response while signed out, and the last one for the
 * signed-in account, kept on the device (localStorage on web, MMKV on
 * mobile) so a fresh page load or cold start begins from the values this
 * device last received. Sign-out drops the signed-in values, so another
 * account never reads them.
 */
export const savedFlagsAtom = atomKvsSync({
  key: 'flags.saved',
  schema: SavedFlags,
  defaultValue: () => noSavedFlags,
});

export const withoutSignedInFlags = (saved: SavedFlags) =>
  saved.signedIn === null ? saved : { ...saved, signedIn: null };

/** `saved` with `flags` recorded for `userId` (`null` = signed out); the same object when nothing changed. */
const withSavedResponse = (
  saved: SavedFlags,
  userId: string | null,
  flags: ClientFlagValues,
  stage: FlagStage,
) => {
  const base = saved.stage === stage ? saved : { ...noSavedFlags, stage };
  if (userId === null)
    return base.signedOut === flags && base.signedIn === null
      ? base
      : { stage, signedOut: flags, signedIn: null };
  return base.signedIn?.userId === userId && base.signedIn.flags === flags
    ? base
    : { ...base, signedIn: { userId, flags } };
};

/**
 * What the saved values say for a viewer: `userId` is the signed-in account,
 * `null` when signed out, `undefined` while the session loads. While it loads
 * the signed-in values are used if there are any: sign-out drops them, so they
 * belong to the account still signed in. Values saved from another stage's
 * backend are ignored.
 */
export const savedFlagView = (
  saved: SavedFlags,
  userId: string | null | undefined,
  stage: FlagStage,
) => {
  if (saved.stage !== stage)
    return { viewer: undefined, signedOut: undefined, stage };
  const signedOut = saved.signedOut ?? undefined;
  return {
    viewer:
      userId === null
        ? signedOut
        : userId === undefined
          ? (saved.signedIn?.flags ?? signedOut)
          : saved.signedIn?.userId === userId
            ? saved.signedIn.flags
            : undefined,
    signedOut,
    stage,
  };
};

/**
 * The viewer's flags for rendering: `fresh` (this viewer's `Flags.Get`
 * response) when it has arrived, else the values saved for this viewer, plus
 * the signed-out values a flag may fall back to. Saves every fresh response
 * under the viewer the server decided it for, and drops the signed-in values
 * once the viewer is signed out.
 */
export function useSavedClientFlags(input: {
  readonly userId: string | null | undefined;
  readonly fresh: FlagsResponse | undefined;
  readonly stage: FlagStage;
}) {
  const [saved, setSaved] = useAtom(savedFlagsAtom);
  const { userId, fresh, stage } = input;
  useEffect(() => {
    if (userId === undefined) return;
    const next =
      fresh === undefined
        ? userId === null
          ? withoutSignedInFlags(saved)
          : saved
        : withSavedResponse(saved, fresh.userId, fresh.flags, stage);
    if (next !== saved) setSaved(next);
  }, [fresh, userId, stage, saved, setSaved]);
  const fromSaved = savedFlagView(saved, userId, stage);
  return { ...fromSaved, viewer: fresh?.flags ?? fromSaved.viewer };
}
